import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment as env } from '../../../environments/environment';
import {
  HouseExemplar,
  HouseExemplarKind,
  HouseExemplarLanguage,
} from '../../interfaces/house-exemplar.interface';

/**
 * HTTP client for per-client house-style exemplars.
 *
 * Mirrors ClientCvTemplateService / CvService conventions:
 *   - environment.api prefix
 *   - withCredentials: true so the Sanctum cookie travels
 *   - Returns Observables for RxJS composition
 *
 * The backend list endpoint returns a plain array (not a paginated
 * envelope), so list() resolves to HouseExemplar[] directly.
 */
@Injectable({ providedIn: 'root' })
export class HouseExemplarService {

  private readonly httpOptions = { withCredentials: true };

  constructor(private readonly http: HttpClient) {}

  public list(clientId: number): Observable<HouseExemplar[]> {
    return this.http.get<HouseExemplar[]>(
      `${env.api}/clients/${clientId}/cv/house-exemplars`,
      this.httpOptions
    );
  }

  public upload(
    clientId: number,
    kind: HouseExemplarKind,
    language: HouseExemplarLanguage,
    file: File
  ): Observable<HouseExemplar> {
    const body = new FormData();
    body.append('kind', kind);
    body.append('language', language);
    body.append('file', file);
    return this.http.post<HouseExemplar>(
      `${env.api}/clients/${clientId}/cv/house-exemplars`,
      body,
      this.httpOptions
    );
  }

  public updateGuide(
    clientId: number,
    exemplarId: number,
    styleGuide: string
  ): Observable<HouseExemplar> {
    return this.http.put<HouseExemplar>(
      `${env.api}/clients/${clientId}/cv/house-exemplars/${exemplarId}`,
      { style_guide: styleGuide },
      this.httpOptions
    );
  }

  public activate(clientId: number, exemplarId: number): Observable<HouseExemplar> {
    return this.http.post<HouseExemplar>(
      `${env.api}/clients/${clientId}/cv/house-exemplars/${exemplarId}/activate`,
      {},
      this.httpOptions
    );
  }

  public destroy(clientId: number, exemplarId: number): Observable<void> {
    return this.http.delete<void>(
      `${env.api}/clients/${clientId}/cv/house-exemplars/${exemplarId}`,
      this.httpOptions
    );
  }

  public promote(packageId: number, kind: HouseExemplarKind): Observable<HouseExemplar> {
    return this.http.post<HouseExemplar>(
      `${env.api}/application-packages/${packageId}/promote-exemplar`,
      { kind },
      this.httpOptions
    );
  }
}
